import {
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme, { palette } from "~frontend/theme";

export function RhfTextField<T extends FieldValues>(
  props: TextFieldProps &
    UseControllerProps<T> & {
      optional?: boolean;
      bottomLabel?: string;
      characterLimit?: number;
    }
): ReactElement {
  const { field, fieldState } = useController(props);

  const { characterLimit, ...rest } = props;

  const charsOverLimit =
    characterLimit && field.value ? field.value.length - characterLimit : 0;

  return (
    <MuiTextField
      onChange={field.onChange}
      onBlur={e => {
        const trimmedValue = (e.target.value || "").trim();
        field.onChange({ target: { value: trimmedValue } });
        field.onBlur();
      }}
      value={field.value}
      name={field.name}
      inputRef={field.ref}
      error={Boolean(fieldState.error)}
      helperText={
        fieldState.error ? fieldState.error.message : props.bottomLabel
      }
      {...(rest as TextFieldProps)}
      label={
        <FlexBox
          sx={{ width: "100%" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <FlexBox alignItems="center" gap={1}>
            {props.label}
            {props.optional ? <Typography> (optional)</Typography> : ""}
          </FlexBox>
          {characterLimit ? (
            <FlexBox alignItems="flex-end">
              <Typography
                variant="h4Caps"
                component="div"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: charsOverLimit > 0 ? palette.red : palette.navy,
                }}
              >
                {charsOverLimit > 0
                  ? `${charsOverLimit} character${charsOverLimit > 1 ? "s" : ""} over limit`
                  : `${characterLimit} character limit`}
              </Typography>
            </FlexBox>
          ) : (
            <div />
          )}
        </FlexBox>
      }
      slotProps={{
        inputLabel: {
          sx: { width: "133%", maxWidth: `calc(140% - ${theme.spacing(4)})` },
          shrink: true,
          ...(props.InputLabelProps || props.slotProps?.inputLabel || {}),
        },
        input: {
          ...(props.InputProps || props.slotProps?.input || {}),
        },
      }}
    />
  );
}
